import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
    Upload,
    FileText,
    Loader2,
    Download,
    Pause,
    Play,
    AlertCircle,
    CheckCircle,
    Trash2,
    Info as InfoIcon,
    AlertTriangle,
    Settings,
    Mail,
    Tag,
    Folder,
    RefreshCw,
    X,
    Clock
} from 'lucide-react'
import { apiService } from '../config/api'
import api from '../config/api'
import toast from 'react-hot-toast'
import CSVExportModal from '../components/CSVExportModal'
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useNavigate, Link } from 'react-router-dom';
import SmartPagination from '../components/SmartPagination';
import Portal from '../components/Portal';
import { useAction } from '../contexts/ActionContext';

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'var(--lv-control-bg, white)',
        borderColor: state.isFocused ? '#6366f1' : 'var(--lv-control-border, #374151)',
        boxShadow: state.isFocused ? '0 0 0 2px #6366f133' : 'none',
        borderRadius: '0.5rem',
        minHeight: '2.5rem',
        fontSize: '1rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        color: 'var(--lv-control-text, #111827)'
    }),
    valueContainer: base => ({
        ...base,
        color: 'var(--lv-control-text, #111827)'
    }),
    menu: base => ({
        ...base,
        zIndex: 9999,
        borderRadius: '0.5rem',
        fontSize: '1rem',
        backgroundColor: 'var(--lv-menu-bg, white)',
        color: 'var(--lv-menu-text, #111827)'
    }),
    menuList: base => ({
        ...base,
        backgroundColor: 'var(--lv-menu-bg, white)',
        color: 'var(--lv-menu-text, #111827)'
    }),
    menuPortal: base => ({
        ...base,
        zIndex: 9999,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--lv-option-selected, #e5e7eb)'
            : state.isFocused
                ? 'var(--lv-option-hover, #f3f4f6)'
                : 'var(--lv-option-bg, white)',
        color: 'var(--lv-option-text, #111827)'
    }),
    singleValue: base => ({
        ...base,
        color: 'var(--lv-control-text, #111827)'
    }),
    placeholder: base => ({
        ...base,
        color: 'var(--lv-placeholder, #6b7280)'
    }),
    input: base => ({
        ...base,
        color: 'var(--lv-control-text, #111827)'
    }),
    dropdownIndicator: base => ({
        ...base,
        color: 'var(--lv-indicator, #9ca3af)',
        paddingRight: '0.5rem',
    }),
    clearIndicator: base => ({
        ...base,
        color: 'var(--lv-indicator, #9ca3af)',
        paddingRight: '0.5rem',
    }),
    indicatorSeparator: base => ({
        ...base,
        backgroundColor: 'var(--lv-control-border, #374151)'
    })
};

export default function ListVerification() {
    // Multi-file upload queue: each item = { id, file, group, progress, status, jobId, abortController, error }
    const [uploadQueue, setUploadQueue] = useState([])
    const [isUploadingQueue, setIsUploadingQueue] = useState(false)
    const uploadQueueRef = useRef([])

    // Legacy single-file state kept for poll/progress compatibility
    const [bulkResults, setBulkResults] = useState(null)
    const [isBulkVerifying, setIsBulkVerifying] = useState(false)
    const [progress, setProgress] = useState(null)
    const [jobId, setJobId] = useState(null)
    const [jobs, setJobs] = useState([])
    const [jobsTotal, setJobsTotal] = useState(0)
    const [jobSearchTerm, setJobSearchTerm] = useState('')
    const [debouncedJobSearch, setDebouncedJobSearch] = useState('')
    const [workerConcurrency, setWorkerConcurrency] = useState(2)
    const [isLoadingConcurrency, setIsLoadingConcurrency] = useState(true)
    const pollingRef = useRef(null)
    const jobsFirstFetch = useRef(true)
    const [maxUploadSizeMB, setMaxUploadSizeMB] = useState(10)
    const [isUploading, setIsUploading] = useState(false)
    const [jobsLoading, setJobsLoading] = useState(true)

    // CSV Export modal state
    const [exportModalOpen, setExportModalOpen] = useState(false)
    const [exportScope, setExportScope] = useState('page')
    const [selectedTypes, setSelectedTypes] = useState(['ALL'])
    const [selectedJobForExport, setSelectedJobForExport] = useState(null)

    // Delete confirmation modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [jobToDelete, setJobToDelete] = useState(null)
    const [deletingJobId, setDeletingJobId] = useState(null) // Track which job is being deleted
    const [deletingJobIds, setDeletingJobIds] = useState(new Set()) // Track multiple jobs being deleted
    const [isDeletingAny, setIsDeletingAny] = useState(false) // Track if any deletion is in progress

    // Retry unknowns state
    const [retryingUnknownsJobIds, setRetryingUnknownsJobIds] = useState(new Set())

    // Force-complete state
    const [forceCompletingJob, setForceCompletingJob] = useState(null)
    const [forceCompleteModalOpen, setForceCompleteModalOpen] = useState(false)
    const pendingForceCompleteRef = useRef(null)

    // Starting job state (loading indicator for Start button)
    const [startingJobId, setStartingJobId] = useState(null)

    // Pause button loading state
    const [pausingJobId, setPausingJobId] = useState(null)

    // Bulk delete state
    const [selectedJobs, setSelectedJobs] = useState(new Set())
    const [selectAll, setSelectAll] = useState(false)
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false)
    const selectedJobsRef = useRef(new Set()) // Ref to track current selection without causing re-renders

    // Pagination state for jobs
    const [jobPage, setJobPage] = useState(1)
    const jobsPerPage = 12 // Increased for better overview

    const totalJobPages = Math.max(1, Math.ceil((jobsTotal || 0) / jobsPerPage) || 1)

    useEffect(() => {
        const t = setTimeout(() => setDebouncedJobSearch(jobSearchTerm.trim()), 400)
        return () => clearTimeout(t)
    }, [jobSearchTerm])

    useEffect(() => {
        setJobPage(1)
    }, [debouncedJobSearch])

    // Add state for cancel confirmation at the top
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [jobToCancel, setJobToCancel] = useState(null);
    const [isCancellingJob, setIsCancellingJob] = useState(false);

    // State to track which job to highlight after pagination updates
    const [selectedJobToHighlight, setSelectedJobToHighlight] = useState(null);
    const [pendingPage, setPendingPage] = useState(null);

    // Upload modal state
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const [jobToFocusAfterRefresh, setJobToFocusAfterRefresh] = useState(null);
    const [previewItems, setPreviewItems] = useState([]);
    const [previewJobId, setPreviewJobId] = useState(null);

    const navigate = useNavigate();
    const { runAction } = useAction();

    // Settings check state - initialize as not configured to prevent race condition
    const [settingsCheck, setSettingsCheck] = useState(null)
    const [settingsCheckLoading, setSettingsCheckLoading] = useState(true)
    const [onlineNodesCount, setOnlineNodesCount] = useState(0)
    const [isNodesCheckLoading, setIsNodesCheckLoading] = useState(true)

    // Group / Category states
    const [groups, setGroups] = useState([])
    const [ungroupedCount, setUngroupedCount] = useState(0)
    const [selectedGroupFilter, setSelectedGroupFilter] = useState('all')
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('all')
    const [uploadGroupName, setUploadGroupName] = useState('')
    const [editGroupModalOpen, setEditGroupModalOpen] = useState(false)
    const [editingJob, setEditingJob] = useState(null)
    const [editGroupName, setEditGroupName] = useState('')

    // Helper: update a single queue item by its id
    const updateQueueItem = (id, patch) => {
        setUploadQueue(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item))
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv']
        },
        multiple: true,
        onDrop: (acceptedFiles) => {
            const tooBig = acceptedFiles.filter(f => f.size > maxUploadSizeMB * 1024 * 1024)
            if (tooBig.length > 0) {
                toast.error(`${tooBig.length} file(s) exceed the ${maxUploadSizeMB}MB limit and were skipped.`)
            }
            const valid = acceptedFiles.filter(f => f.size <= maxUploadSizeMB * 1024 * 1024)
            if (valid.length === 0) return
            const newItems = valid.map(file => ({
                id: Math.random().toString(36).slice(2),
                file,
                group: uploadGroupName,
                progress: 0,
                status: 'pending', // pending | uploading | done | error | cancelled
                jobId: null,
                abortController: null,
                error: null
            }))
            setUploadQueue(prev => {
                const updated = [...prev, ...newItems]
                uploadQueueRef.current = updated
                return updated
            })
        }
    })

    // On mount, fetch jobs, concurrency, and max upload size
    useEffect(() => {
        const loadConcurrencySettings = async () => {
            try {
                const concurrency = await apiService.getWorkerConcurrency()
                setWorkerConcurrency(concurrency)
            } catch (error) {
                // no-op
            } finally {
                setIsLoadingConcurrency(false)
            }
        }
        const loadMaxUploadSize = async () => {
            try {
                const response = await apiService.getSettings()
                const mb = response.data.data?.uploads?.max_upload_size_mb || 100
                setMaxUploadSizeMB(mb)
            } catch (error) {
                setMaxUploadSizeMB(10)
            }
        }
        const checkSettings = async () => {
            try {
                const response = await apiService.checkSettings()
                setSettingsCheck(response.data.data)
            } catch (error) {
                // no-op
            } finally {
                setSettingsCheckLoading(false)
            }
        }
        const checkOnlineNodes = async () => {
            try {
                const response = await apiService.getSatellites()
                const nodes = response.data.data || []
                // A node is "active" if it's not offline/error AND not manually paused by the user.
                // 'rate_limited' and 'maintenance' are still operational states.
                const onlineAndActive = nodes.filter(n =>
                    !n.is_paused &&
                    !['offline', 'error'].includes(n.status)
                ).length
                setOnlineNodesCount(onlineAndActive)
            } catch (error) {
                // no-op
                setOnlineNodesCount(0)
            } finally {
                setIsNodesCheckLoading(false)
            }
        }
        const loadGroups = async () => {
            try {
                const response = await api.get('/verify/groups')
                if (response.data.success) {
                    setGroups(response.data.data || [])
                    setUngroupedCount(response.data.ungrouped_count || 0)
                }
            } catch (error) {
                console.error('Failed to load groups:', error)
            }
        }

        loadConcurrencySettings()
        loadMaxUploadSize()
        checkSettings()
        checkOnlineNodes() // call immediately on mount
        loadGroups() // load groups on mount
        // Periodically refresh concurrency and node status every 10 seconds
        const refreshInterval = setInterval(() => {
            loadConcurrencySettings();
            checkOnlineNodes();
        }, 10000);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
            }
            clearInterval(refreshInterval)
        }
    }, [])

    const loadGroups = async () => {
        try {
            const response = await api.get('/verify/groups')
            if (response.data.success) {
                setGroups(response.data.data || [])
            }
        } catch (error) {
            console.error('Failed to load groups:', error)
        }
    }

    const startEditingGroup = (job) => {
        setEditingJob(job);
        setEditGroupName(job.group_name || '');
        setEditGroupModalOpen(true);
    };

    const confirmSaveGroup = async () => {
        if (!editingJob) return;
        try {
            const response = await api.post(`/verify/jobs/${editingJob.job_id}/group`, {
                group_name: editGroupName
            });
            if (response.data.success) {
                toast.success('Group updated successfully');
                setEditGroupModalOpen(false);
                setEditingJob(null);
                await refreshJobs();
                await loadGroups();
            } else {
                toast.error(response.data.message || 'Failed to update group');
            }
        } catch (error) {
            toast.error('Failed to update group');
        }
    };

    // Upload a single queue item by id
    const uploadSingleItem = async (itemId) => {
        const item = uploadQueueRef.current.find(i => i.id === itemId)
        if (!item || item.status !== 'pending') return null

        const abortController = new AbortController()
        updateQueueItem(itemId, { status: 'uploading', abortController, progress: 0, error: null })
        uploadQueueRef.current = uploadQueueRef.current.map(i =>
            i.id === itemId ? { ...i, status: 'uploading', abortController } : i
        )

        const formData = new FormData()
        formData.append('file', item.file)
        if (item.group && item.group.trim()) {
            formData.append('group_name', item.group.trim())
        }

        try {
            const response = await api.post('/verify/upload', formData, {
                timeout: 1800000,
                signal: abortController.signal,
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        updateQueueItem(itemId, { progress: pct })
                        uploadQueueRef.current = uploadQueueRef.current.map(i =>
                            i.id === itemId ? { ...i, progress: pct } : i
                        )
                    }
                }
            })

            if (response.data.success) {
                const newJobId = response.data.data.jobId
                updateQueueItem(itemId, { status: 'done', progress: 100, jobId: newJobId })
                uploadQueueRef.current = uploadQueueRef.current.map(i =>
                    i.id === itemId ? { ...i, status: 'done', progress: 100, jobId: newJobId } : i
                )
                startUploadProgressPolling(newJobId)
                return newJobId
            }
        } catch (error) {
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                updateQueueItem(itemId, { status: 'cancelled', error: 'Cancelled' })
                uploadQueueRef.current = uploadQueueRef.current.map(i =>
                    i.id === itemId ? { ...i, status: 'cancelled' } : i
                )
            } else {
                const msg = error.response?.data?.message || error.message || 'Upload failed'
                updateQueueItem(itemId, { status: 'error', error: msg })
                uploadQueueRef.current = uploadQueueRef.current.map(i =>
                    i.id === itemId ? { ...i, status: 'error', error: msg } : i
                )
                toast.error(`${item.file.name}: ${msg}`)
            }
        }
        return null
    }

    // Upload all pending items in the queue sequentially
    const handleBulkVerification = async () => {
        const pending = uploadQueueRef.current.filter(i => i.status === 'pending')
        if (pending.length === 0) {
            toast.error('Please add at least one CSV file')
            return
        }

        setIsUploadingQueue(true)
        setIsUploading(true)
        let lastJobId = null

        for (const item of pending) {
            // Refresh ref each iteration so we pick up cancellations
            const current = uploadQueueRef.current.find(i => i.id === item.id)
            if (!current || current.status === 'cancelled') continue
            const jid = await uploadSingleItem(item.id)
            if (jid) lastJobId = jid
        }

        await refreshJobs(true)
        await loadGroups()

        if (lastJobId) {
            setJobToFocusAfterRefresh(lastJobId)
            toast.success('All files uploaded successfully!')
        }

        // Auto-close modal after 1.5s if all items are done/error/cancelled
        setTimeout(() => {
            const allDone = uploadQueueRef.current.every(i =>
                ['done', 'error', 'cancelled'].includes(i.status)
            )
            if (allDone) {
                setUploadModalOpen(false)
                setUploadQueue([])
                uploadQueueRef.current = []
                setUploadGroupName('')
            }
            setIsUploadingQueue(false)
            setIsUploading(false)
        }, 1500)
    }

    // Poll upload processing progress
    const startUploadProgressPolling = (jobId) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await api.get(`/verify/jobs/${jobId}`)
                const job = response.data.data

                if (job.status === 'failed') {
                    clearInterval(pollInterval)
                    toast.error(`Upload failed: ${job.error_message || 'Unknown error'}`)
                    await refreshJobs(true)
                } else if (job.status === 'ready') {
                    clearInterval(pollInterval)
                    toast.success('Upload processing completed! Job is ready for verification.')
                    await refreshJobs(true)
                } else if (job.status === 'uploading') {
                    // Show progress for upload processing
                    const progressPercent = job.total_emails > 0 ? Math.round((job.processed_emails / job.total_emails) * 100) : 0
                    // Upload processing logging removed for production
                }
            } catch (error) {
                // no-op
            }
        }, 2000) // Poll every 2 seconds

        // Stop polling after 5 minutes to prevent infinite polling
        setTimeout(() => {
            clearInterval(pollInterval)
        }, 5 * 60 * 1000)
    }

    const handleCancelUpload = (itemId) => {
        const item = uploadQueueRef.current.find(i => i.id === itemId)
        if (item && item.abortController) {
            item.abortController.abort()
        } else {
            // Cancel all pending items
            uploadQueueRef.current.forEach(i => {
                if (i.status === 'pending') {
                    uploadQueueRef.current = uploadQueueRef.current.map(x =>
                        x.id === i.id ? { ...x, status: 'cancelled' } : x
                    )
                    updateQueueItem(i.id, { status: 'cancelled' })
                }
                if (i.abortController && i.status === 'uploading') i.abortController.abort()
            })
            toast('All uploads cancelled')
        }
    }

    const handleCancelJob = async () => {
        try {
            await apiService.api.post('/upload/cancel')
            if (pollingRef.current) clearInterval(pollingRef.current)
            setIsBulkVerifying(false)
            setProgress((prev) => prev ? { ...prev, status: 'cancelled', error: 'Job cancelled by user.' } : null)
            toast.success('Bulk verification cancelled')
        } catch (error) {
            toast.error('Failed to cancel job')
        }
    }

    // Cancel a specific job by ID
    const handleCancelJobById = async (jobId) => {
        setJobToCancel(jobId);
        setCancelModalOpen(true);
    }

    const handlePauseJob = async (jobId) => {
        if (pausingJobId) return
        setPausingJobId(jobId)
        await runAction(async () => {
            await apiService.pauseJob(jobId)
            toast.success('Job paused')
            await refreshJobs()
            setJobToFocusAfterRefresh(jobId);
        }, { message: 'Pausing job...' })
        setPausingJobId(null)
    }

    const handleResumeJob = async (jobId) => {
        if (startingJobId) return
        setStartingJobId(jobId)
        await runAction(async () => {
            const res = await apiService.resumeJob(jobId)
            const newStatus = res.data?.data?.status || res.data?.status
            if (newStatus === 'queued') {
                toast.success('Job queued — will auto-start when a slot opens')
            } else {
                toast.success('Job resumed')
            }
            await refreshJobs()
            setJobToFocusAfterRefresh(jobId);
        }, { message: 'Resuming job...' })
        setStartingJobId(null)
    }

    const handleRetryUnknowns = async (jobId) => {
        if (retryingUnknownsJobIds.has(jobId)) return
        setRetryingUnknownsJobIds(prev => new Set(prev).add(jobId))
        await runAction(async () => {
            const toastId = `retry-unknowns-${jobId}`
            toast.loading('Queuing Unknown emails for re-verification...', { id: toastId })
            try {
                const res = await apiService.retryUnknowns(jobId)
                const count = res.data?.reset || 0
                if (count === 0) {
                    toast.success('No Unknown emails found to retry.', { id: toastId })
                } else {
                    toast.success(`${count.toLocaleString()} Unknown email(s) queued for re-verification!`, { id: toastId })
                }
                await refreshJobs()
                setJobToFocusAfterRefresh(jobId)
            } catch (error) {
                toast.error(error?.response?.data?.message || 'Failed to retry unknowns.', { id: toastId })
            }
        }, { message: 'Retrying unknown emails...' })
        setRetryingUnknownsJobIds(prev => { const s = new Set(prev); s.delete(jobId); return s })
    }

    const openForceCompleteModal = (jobId, stuckRemaining) => {
        if (forceCompletingJob === jobId) return
        pendingForceCompleteRef.current = { jobId, stuckRemaining }
        setForceCompleteModalOpen(true)
    }

    const confirmForceComplete = async () => {
        const info = pendingForceCompleteRef.current
        if (!info) return
        const { jobId } = info
        if (forceCompletingJob) return
        setForceCompleteModalOpen(false)
        pendingForceCompleteRef.current = null
        setForceCompletingJob(jobId)
        await runAction(async () => {
            const toastId = `force-complete-${jobId}`
            toast.loading('Force-completing job...', { id: toastId })
            try {
                const res = await apiService.forceCompleteJob(jobId)
                toast.success(res.data?.message || 'Job force-completed.', { id: toastId })
                await refreshJobs()
                setJobToFocusAfterRefresh(jobId)
                if (pollingRef.current) {
                    clearInterval(pollingRef.current)
                    pollingRef.current = null
                }
                setIsBulkVerifying(false)
                setProgress(null)
            } catch (error) {
                toast.error(error?.response?.data?.message || 'Failed to force-complete job.', { id: toastId })
            }
        }, { message: 'Force-completing job...' })
        setForceCompletingJob(null)
    }

    const handleDeleteJob = (jobId) => {
        if (isDeletingAny) {
            toast.error('Please wait for the current deletion to complete')
            return
        }
        setJobToDelete(jobId)
        setDeleteModalOpen(true)
    }

    const confirmDeleteJob = async () => {
        if (!jobToDelete || isDeletingAny) return
        setIsDeletingAny(true)
        setDeletingJobId(jobToDelete)
        setDeleteModalOpen(false)
        const currentJobToDelete = jobToDelete
        setJobToDelete(null)

        await runAction(async () => {
            // Track deletion start time for UI polling logic
            window.lastDeleteStartTime = Date.now()

            // Find the job object
            const job = jobs.find(j => j.job_id === currentJobToDelete)

            // If job is uploading, pause it first and wait for pause
            if (job && job.status === 'uploading') {
                toast.loading('Pausing upload before deleting...', { id: 'pauseJob' })
                try {
                    await apiService.pauseJob(currentJobToDelete)
                    // Poll until job is actually paused
                    const start = Date.now()
                    const TIMEOUT = 15000 // 15 seconds
                    let paused = false
                    while (Date.now() - start < TIMEOUT) {
                        await refreshJobs(true)
                        const updatedJob = jobs.find(j => j.job_id === currentJobToDelete)
                        if (updatedJob && updatedJob.status === 'paused') {
                            paused = true
                            break
                        }
                        await new Promise(res => setTimeout(res, 1000))
                    }
                    if (paused) {
                        toast.success('Upload paused. Deleting job...', { id: 'pauseJob' })
                    } else {
                        toast.error('Pausing upload timed out. Cannot delete job.', { id: 'pauseJob' })
                        setDeletingJobId(null)
                        setIsDeletingAny(false)
                        return
                    }
                } catch (e) {
                    // no-op
                    setDeletingJobId(null)
                    setIsDeletingAny(false)
                    return
                }
            }

            // Start deletion
            toast.loading('Deleting job...', { id: 'deleteJob' })
            await apiService.deleteJob(currentJobToDelete)

            // For small jobs, expect immediate completion
            const totalEmails = job?.total_emails || 0
            if (totalEmails < 1000) {
                toast.success('Job deleted successfully', { id: 'deleteJob' })
                setDeletingJobId(null)
                setIsDeletingAny(false)
                await refreshJobs(true)
                return
            }

            // For large jobs, start polling with longer intervals
            // Immediately mark job as deleting in UI
            setJobs(prev => prev.map(j => j.job_id === currentJobToDelete ? { ...j, status: 'deleting' } : j))
            setDeletingJobIds(prev => new Set(prev).add(currentJobToDelete))

            // Optimized polling with progressive intervals and better timeout handling
            const startTime = Date.now()
            const MAX_TIMEOUT = 120000 // 2 minutes for large jobs
            let pollInterval = 5000 // Start with 5 seconds for large jobs
            let consecutiveNotFound = 0

            const pollDeletion = async () => {
                try {
                    await refreshJobs(false) // Don't show loading spinner during polling
                    const currentJob = jobs.find(j => j.job_id === currentJobToDelete)

                    if (!currentJob) {
                        consecutiveNotFound++
                        // Confirm deletion with multiple checks to prevent false positives
                        if (consecutiveNotFound >= 2) {
                            toast.success('Job deleted successfully', { id: 'deleteJob' })
                            setDeletingJobId(null)
                            setIsDeletingAny(false)
                            setDeletingJobIds(prev => {
                                const newSet = new Set(prev)
                                newSet.delete(currentJobToDelete)
                                return newSet
                            })
                            return
                        }
                    } else {
                        consecutiveNotFound = 0
                        // Check if job is in 'deleting' status or shows progress in error_message
                        if (currentJob.status === 'deleting' || (currentJob.error_message && currentJob.error_message.includes('Deleting:'))) {
                            toast.loading(`${currentJob.error_message || 'Deleting job...'}`, { id: 'deleteJob' })
                        }
                    }

                    // Check timeout
                    if (Date.now() - startTime > MAX_TIMEOUT) {
                        toast.error('Job deletion is taking longer than expected. Please check back later.', { id: 'deleteJob' })
                        setDeletingJobId(null)
                        setIsDeletingAny(false)
                        setDeletingJobIds(prev => {
                            const newSet = new Set(prev)
                            newSet.delete(currentJobToDelete)
                            return newSet
                        })
                        return
                    }

                    // Progressive polling intervals
                    if (Date.now() - startTime > 30000) {
                        pollInterval = 10000 // Slow down to 10 seconds after 30 seconds
                    } else if (Date.now() - startTime > 10000) {
                        pollInterval = 7000 // Slow down to 7 seconds after 10 seconds
                    }

                    setTimeout(pollDeletion, pollInterval)
                } catch (error) {
                    // no-op
                    // On error, wait longer before next poll
                    setTimeout(pollDeletion, Math.min(pollInterval * 2, 15000))
                }
            }

            // Start polling after a brief delay
            setTimeout(pollDeletion, pollInterval)
        }, { message: 'Deleting job...' })
    }

    // Bulk delete functions
    const handleBulkDelete = () => {
        if (selectedJobs.size === 0) {
            toast.error('Please select jobs to delete')
            return
        }
        if (isDeletingAny) {
            toast.error('Please wait for the current deletion to complete')
            return
        }
        setBulkDeleteModalOpen(true)
    }

    const confirmBulkDelete = async () => {
        if (selectedJobs.size === 0 || isDeletingAny) return
        setIsDeletingAny(true)
        setDeletingJobIds(new Set(selectedJobs))
        setBulkDeleteModalOpen(false)

        const jobIdsArray = Array.from(selectedJobs)
        const totalJobs = jobIdsArray.length
        let successCount = 0
        let failCount = 0

        await runAction(async () => {
            toast.loading(`Deleting ${totalJobs} jobs...`, { id: 'bulkDelete' })

            // Delete jobs in parallel batches to speed up process
            const batchSize = 3 // Process 3 jobs at a time

            for (let i = 0; i < jobIdsArray.length; i += batchSize) {
                const batch = jobIdsArray.slice(i, i + batchSize)
                const promises = batch.map(async (jobId) => {
                    try {
                        await apiService.deleteJob(jobId)
                        return { success: true, jobId }
                    } catch (error) {
                        // no-op
                        return { success: false, jobId, error }
                    }
                })

                const results = await Promise.all(promises)
                results.forEach(result => {
                    if (result.success) {
                        successCount++
                    } else {
                        failCount++
                    }
                })

                // Update progress toast
                const completed = successCount + failCount
                if (completed < totalJobs) {
                    toast.loading(`Deleting jobs... ${completed}/${totalJobs}`, { id: 'bulkDelete' })
                }
            }
        }, { message: `Deleting ${totalJobs} job(s)...` })

        // Clear selection immediately since we've submitted all delete requests
        const newSelected = new Set()
        setSelectedJobs(newSelected)
        selectedJobsRef.current = newSelected
        setSelectAll(false)

        // Poll for completion with shorter, more frequent checks for better UX
        const start = Date.now()
        const TIMEOUT = 60000 // 60 seconds for bulk operations
        let pollCount = 0
        const MAX_POLLS = 30 // Maximum number of polling attempts

        const checkDeletion = async () => {
            pollCount++
            try {
                await refreshJobs(false) // Don't show loading spinner during polling
                const stillExisting = jobs.filter(j => jobIdsArray.includes(j.job_id))

                if (stillExisting.length === 0) {
                    // All jobs deleted successfully
                    toast.success(`Successfully deleted ${successCount} jobs${failCount > 0 ? `, ${failCount} failed` : ''}`, { id: 'bulkDelete' })
                    setDeletingJobIds(new Set())
                    setIsDeletingAny(false)
                    return
                }

                // Check timeout conditions
                if (Date.now() - start > TIMEOUT || pollCount >= MAX_POLLS) {
                    const remainingCount = stillExisting.length
                    toast.error(`Deletion timeout. ${successCount} deleted, ${remainingCount} remaining, ${failCount} failed. Please refresh.`, { id: 'bulkDelete' })
                    setDeletingJobIds(new Set())
                    setIsDeletingAny(false)
                    return
                }

                // Continue polling with shorter intervals for better feedback
                setTimeout(checkDeletion, 2000) // Check every 2 seconds
            } catch (error) {
                // no-op
                // On error, retry with longer delay
                if (pollCount < MAX_POLLS && Date.now() - start < TIMEOUT) {
                    setTimeout(checkDeletion, 5000)
                } else {
                    toast.error('Error checking deletion status. Please refresh the page.', { id: 'bulkDelete' })
                    setDeletingJobIds(new Set())
                    setIsDeletingAny(false)
                }
            }
        }

        // Start polling after a brief delay to allow the delete requests to process
        setTimeout(checkDeletion, 3000)
    }

    const [bulkActionLoading, setBulkActionLoading] = useState(null) // 'pause' | 'start' | null

    const handleBulkPause = async () => {
        if (selectedJobs.size === 0) return
        setBulkActionLoading('pause')
        await runAction(async () => {
            const res = await apiService.batchAction('pause', Array.from(selectedJobs))
            toast.success(res.data?.message || 'Jobs paused')
            await refreshJobs()
        }, { message: `Pausing ${selectedJobs.size} job(s)...` })
        setBulkActionLoading(null)
    }

    const handleBulkStart = async () => {
        if (selectedJobs.size === 0) return
        setBulkActionLoading('start')
        await runAction(async () => {
            const res = await apiService.batchAction('start', Array.from(selectedJobs))
            toast.success(res.data?.message || 'Jobs started')
            await refreshJobs()
        }, { message: `Starting ${selectedJobs.size} job(s)...` })
        setBulkActionLoading(null)
    }

    const handleSelectJob = (jobId) => {
        const newSelected = new Set(selectedJobs)
        if (newSelected.has(jobId)) {
            newSelected.delete(jobId)
        } else {
            newSelected.add(jobId)
        }
        setSelectedJobs(newSelected)
        selectedJobsRef.current = newSelected
        setSelectAll(newSelected.size === jobs.length)
    }

    const handleSelectAll = () => {
        if (selectAll) {
            const newSelected = new Set()
            setSelectedJobs(newSelected)
            selectedJobsRef.current = newSelected
            setSelectAll(false)
        } else {
            const newSelected = new Set(jobs.map(j => j.job_id))
            setSelectedJobs(newSelected)
            selectedJobsRef.current = newSelected
            setSelectAll(true)
        }
    }

    const handleDownloadJob = (job) => {
        setSelectedJobForExport(job)
        setExportModalOpen(true)
        setJobToFocusAfterRefresh(job.job_id);
    }

    const handleExportDownload = async ({ exportScope, selectedTypes }) => {
        if (!selectedJobForExport) return

        try {
            // Fetch job items based on export scope
            let jobItems = []

            if (exportScope === 'page') {
                // Get first page of items
                const response = await api.get(`/verify/jobs/${selectedJobForExport.job_id}/items?limit=50&offset=0`)
                jobItems = response.data.data.items || []
            } else if (exportScope === 'all') {
                // Get all items for this job
                const response = await api.get(`/verify/jobs/${selectedJobForExport.job_id}/items?limit=10000&offset=0`)
                jobItems = response.data.data.items || []
            } else if (exportScope === 'custom') {
                // Get all items and filter by selected types
                const response = await api.get(`/verify/jobs/${selectedJobForExport.job_id}/items?limit=10000&offset=0`)
                let allItems = response.data.data.items || []

                if (!selectedTypes.includes('ALL')) {
                    allItems = allItems.filter(item =>
                        selectedTypes.includes(item.status) || selectedTypes.includes(item.type)
                    )
                }
                jobItems = allItems
            }

            // Generate CSV content
            const allColumns = new Set()
            jobItems.forEach(item => {
                let extraColumns = null
                if (item.extra_columns && typeof item.extra_columns === 'string') {
                    try {
                        extraColumns = JSON.parse(item.extra_columns)
                    } catch (e) {
                        extraColumns = null
                    }
                } else if (item.extra_columns && typeof item.extra_columns === 'object') {
                    extraColumns = item.extra_columns
                }
                if (extraColumns) {
                    Object.keys(extraColumns).forEach(key => {
                        if (key !== 'tags') {
                            allColumns.add(key)
                        }
                    })
                }
            })

            const originalColumns = Array.from(allColumns).sort()
            const verificationColumns = ['Status', 'Email Type', 'Safe to Send']
            const headers = ['email', ...originalColumns, ...verificationColumns]

            const csvContent = [
                headers.join(','),
                ...jobItems.map(item => {
                    let extraColumns = null
                    if (item.extra_columns && typeof item.extra_columns === 'string') {
                        try {
                            extraColumns = JSON.parse(item.extra_columns)
                        } catch (e) {
                            extraColumns = null
                        }
                    } else if (item.extra_columns && typeof item.extra_columns === 'object') {
                        extraColumns = item.extra_columns
                    }

                    const row = [item.email]

                    originalColumns.forEach(column => {
                        const value = extraColumns && extraColumns[column] ? extraColumns[column] : ''
                        const escapedValue = typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))
                            ? `"${value.replace(/"/g, '""')}"`
                            : value
                        row.push(escapedValue)
                    })

                    row.push(
                        item.status,
                        item.type || '',
                        item.status === 'Valid' ? 'Yes' : (item.status === 'Catch All' ? 'Risky' : 'No')
                    )

                    return row.join(',')
                })
            ].join('\n')

            // Download the CSV
            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${selectedJobForExport.job_name}_export_${new Date().toISOString().split('T')[0]}.csv`
            a.click()
            window.URL.revokeObjectURL(url)

            setExportModalOpen(false)
            setSelectedJobForExport(null)
            toast.success('Export completed successfully')
        } catch (error) {
            // no-op
            toast.error('Failed to export job data')
        }
    }

    const handleStartJob = async (jobId) => {
        if (startingJobId) return
        setJobPage(1)
        setPendingPage(1)
        setSelectedJobToHighlight(jobId)
        setStartingJobId(jobId)
        await runAction(async () => {
            const res = await apiService.startJob(jobId)
            const newStatus = res.data?.data?.status || res.data?.status

            if (newStatus === 'queued') {
                // Backend queued the job — don't poll, just refresh
                const msg = res.data?.data?.message || res.data?.message || 'Job queued. Will auto-start when a slot opens.'
                toast.success(msg)
                await refreshJobs()
                setJobToFocusAfterRefresh(jobId)
            } else {
                // Job started — begin polling
                setJobId(jobId)
                setIsBulkVerifying(true)
                if (pollingRef.current) clearInterval(pollingRef.current)
                pollingRef.current = setInterval(() => pollProgress(jobId), 2000)
                toast.success('Verification started!')
                await refreshJobs()
                setJobToFocusAfterRefresh(jobId)
            }
        }, { message: 'Starting job...' })
        setStartingJobId(null)
    }

    const loadJobsFromApi = useCallback(async (
        page,
        searchStr,
        showLoading = false,
        preserveSelection = true,
        queryParams = null
    ) => {
        if (showLoading) setJobsLoading(true)
        try {
            let response
            if (queryParams && typeof queryParams === 'object' && Object.keys(queryParams).length > 0) {
                response = await apiService.getJobs({
                    ...queryParams,
                    ...(selectedGroupFilter && selectedGroupFilter !== 'all' ? { group: selectedGroupFilter } : {})
                })
            } else {
                response = await apiService.getJobs({
                    limit: jobsPerPage,
                    offset: (page - 1) * jobsPerPage,
                    ...(searchStr ? { search: searchStr } : {}),
                    ...(selectedGroupFilter && selectedGroupFilter !== 'all' ? { group: selectedGroupFilter } : {}),
                    ...(selectedStatusFilter && selectedStatusFilter !== 'all' ? { status: selectedStatusFilter } : {})
                })
            }

            const responseData = response.data.data || {}
            let newJobs

            if (Array.isArray(responseData)) {
                newJobs = responseData
                setJobsTotal(newJobs.length)
            } else if (responseData.jobs && Array.isArray(responseData.jobs)) {
                newJobs = responseData.jobs
                setJobsTotal(Number(responseData.pagination?.total ?? newJobs.length))
            } else {
                newJobs = []
                setJobsTotal(0)
            }

            // Normalize empty/null status to 'ready' (defense against DB edge cases)
            for (const j of newJobs) {
                if (!j.status) j.status = 'ready';
            }

            setJobs(newJobs)

            if (preserveSelection) {
                const existingJobIds = new Set(newJobs.map(j => j.job_id))
                const updatedSelectedJobs = new Set(Array.from(selectedJobsRef.current).filter(id => existingJobIds.has(id)))
                setSelectedJobs(updatedSelectedJobs)
                selectedJobsRef.current = updatedSelectedJobs
                setSelectAll(updatedSelectedJobs.size === newJobs.length && newJobs.length > 0)
            }
        } catch (error) {
            setJobs([])
            setJobsTotal(0)
        } finally {
            if (showLoading) setTimeout(() => setJobsLoading(false), 500)
        }
    }, [jobsPerPage, selectedGroupFilter, selectedStatusFilter])

    const refreshJobs = useCallback(async (showLoading = false, preserveSelection = true, queryParams = {}) => {
        await loadJobsFromApi(jobPage, debouncedJobSearch, showLoading, preserveSelection, queryParams)
    }, [loadJobsFromApi, jobPage, debouncedJobSearch])

    useEffect(() => {
        const showSpinner = jobsFirstFetch.current && jobPage === 1 && !debouncedJobSearch
        jobsFirstFetch.current = false
        loadJobsFromApi(jobPage, debouncedJobSearch, showSpinner)
    }, [jobPage, debouncedJobSearch, selectedGroupFilter, selectedStatusFilter, loadJobsFromApi])

    // Check if we can start a new job based on concurrency limits
    // Poll job progress with robust error handling
    const pollProgress = async (jobId) => {
        try {
            const response = await apiService.api.get(`/upload/progress/${jobId}`)
            setProgress(response.data.data)
            if (
                response.data.data.status === 'completed' ||
                response.data.data.status === 'error' ||
                response.data.data.status === 'cancelled' ||
                response.data.data.status === 'paused' // stop polling if paused
            ) {
                if (pollingRef.current) {
                    clearInterval(pollingRef.current)
                    pollingRef.current = null
                }
                setIsBulkVerifying(false)
                setJobId(null)
                if (response.data.data.status === 'completed') {
                    setBulkResults({
                        total: response.data.data.total,
                        results: response.data.data.results || []
                    })
                }
                setProgress(null)
                return;
            }
        } catch (error) {
            // no-op
        }
    }

    const exportResults = async (format = 'csv') => {
        try {
            const response = await apiService.exportResults(null, format)
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `verification-results-${Date.now()}.${format}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success(`Results exported as ${format.toUpperCase()}`)
        } catch (error) {
            // no-op
            toast.error('Failed to export results')
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 'valid':
                return <span className="text-success-500">✓</span>
            case 'invalid':
                return <span className="text-danger-500">✗</span>
            case 'disposable':
            case 'catch-all':
                return <span className="text-warning-500">!</span>
            case 'role-based':
                return <span className="text-primary-500">@</span>
            case 'uploading':
                return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            case 'processing':
                return <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
            case 'ready':
                return <span className="text-green-500">Ready</span>
            case 'pending':
                return <span className="text-gray-500">Pending</span>
            case 'completed':
                return <CheckCircle className="h-4 w-4 text-green-500" />
            case 'failed':
                return <AlertCircle className="h-4 w-4 text-red-500" />
            case 'cancelled':
                return <span className="text-gray-500">Cancelled</span>
            case 'paused':
                return <Pause className="h-4 w-4 text-yellow-500" />
            default:
                return <span className="text-gray-500">Unknown</span>
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'valid':
                return 'status-valid'
            case 'invalid':
                return 'status-invalid'
            case 'disposable':
            case 'catch-all':
                return 'status-disposable'
            case 'role-based':
                return 'status-role-based'
            default:
                return 'status-unknown'
        }
    }

    const getStatusText = (job) => {
        // Use both upload_status and status (verification_status)
        if (job.upload_status === 'pending') return 'Queued for Upload';
        if (job.upload_status === 'processing') return 'Uploading...';
        if (job.upload_status === 'failed') return 'Upload Failed';
        if (job.upload_status === 'completed' && (job.status === 'pending' || job.status === 'ready')) return 'Ready to Verify';
        if (job.status === 'uploading') return 'Processing Upload...';
        if (job.status === 'uploading_paused') return 'Upload Paused';

        // Custom override: If processing but 100% done, show as Completed
        if (job.status === 'processing' && job.is_greylisting_wait) return 'Greylisting Wait';
        if (job.status === 'processing' && job.total_emails > 0 && job.processed_emails >= job.total_emails && !job.is_greylisting_wait) {
            return 'Completed';
        }

        if (job.status === 'processing') return 'Processing';
        if (job.status === 'queued') return 'Queued';
        if (job.status === 'completed') return 'Completed';
        if (job.status === 'failed') return 'Failed';
        if (job.status === 'cancelled') return 'Cancelled';
        if (job.status === 'paused') return 'Paused';
        if (job.status === 'ready') return 'Ready to Verify';
        if (job.status === 'pending') return 'Ready to Verify';
        return job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : 'Ready to Verify';
    };

    // Use DB-driven liveStats
    const liveStats = progress && progress.liveStats ? progress.liveStats : {}
    const verifiedPercent = progress && progress.total ? Math.round((progress.processed / progress.total) * 100) : 0

    // Helper: Should show progress bar and percent done
    const shouldShowProgress = (job) => {
        // Only show progress bar if job is processing, ready, or paused (not just pending after upload)
        return ['processing', 'queued', 'ready', 'paused'].includes(job.status);
    };

    // Helper: Should show 100% done badge
    const shouldShow100PercentDone = (job) => {
        // Only show 100% done if job is completed, or processing and processed_emails === total_emails and NOT waiting for greylisting
        if (job.status === 'completed') return true;
        if (job.status === 'processing' && job.processed_emails === job.total_emails && !job.is_greylisting_wait) return true;
        return false;
    };

    // Helper to get live or summary stats for a job
    const getJobStats = (job) => {
        // If this job is currently being processed and matches progress, use liveStats
        if (progress && job.job_id === jobId && isBulkVerifying && progress.liveStats) {
            return {
                valid: progress.liveStats['Valid'] || 0,
                invalid: progress.liveStats['Invalid'] || 0,
                catch_all: progress.liveStats['Catch All'] || 0,
                unknown: progress.liveStats['Unknown'] || 0,
                rate_limited: progress.liveStats['Rate Limited'] || 0,
                skipped: progress.liveStats['Skipped'] || 0,
                syntax_error: progress.liveStats['Syntax Error'] || 0,
                disposable: progress.liveStats['Disposable'] || 0,
                free_account: progress.liveStats['Free Account'] || 0,
                role_account: progress.liveStats['Role-Based'] || 0,
                awaiting_imap: progress.liveStats['Awaiting IMAP'] || 0,
                processed: progress.processed || 0,
                total: progress.total || job.total_emails || 0
            };
        }
        // Otherwise, use job summary fields (corrected field names)
        return {
            valid: job.valid_count || 0,
            invalid: job.invalid_count || 0,
            catch_all: job.catch_all_count || 0,
            unknown: job.unknown_count || 0,
            rate_limited: job.rate_limited_count || 0,
            awaiting_imap: job.awaiting_imap_count || 0,
            skipped: job.skipped_count || 0,
            syntax_error: job.syntax_error_count || 0,
            disposable: job.disposable_count || 0,
            free_account: job.free_account_count || 0,
            role_account: job.role_account_count || 0,
            processed:
                (job.valid_count || 0) +
                (job.invalid_count || 0) +
                (job.catch_all_count || 0) +
                (job.unknown_count || 0) +
                (job.rate_limited_count || 0) +
                (job.skipped_count || 0) +
                (job.syntax_error_count || 0) +
                (job.awaiting_imap_count || 0),
            total: job.total_emails || 0
        };
    };

    // useEffect cleanup: always clear polling interval on unmount or jobId change
    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current)
                pollingRef.current = null
            }
        }
    }, [jobId])



    // Poll for live stats during processing
    useEffect(() => {
        if (!jobId || !isBulkVerifying) return;

        // Dynamic polling frequency based on job size
        const getPollingFrequency = (totalEmails) => {
            if (totalEmails > 100000) return 10000; // 10 seconds for very large jobs
            if (totalEmails > 50000) return 5000;   // 5 seconds for large jobs
            return 2000; // 2 seconds for smaller jobs
        };

        const pollStats = async () => {
            try {
                const response = await apiService.api.get(`/upload/progress/${jobId}`);
                const progressData = response.data.data;
                setProgress(progressData);

                // Update jobs list with live stats
                setJobs(prevJobs => prevJobs.map(j =>
                    j.job_id === jobId
                        ? {
                            ...j,
                            status: progressData.status,
                            processed_emails: progressData.processed,
                            valid_count: progressData.liveStats?.Valid || 0,
                            invalid_count: progressData.liveStats?.Invalid || 0,
                            catch_all_count: progressData.liveStats?.['Catch All'] || 0,
                            unknown_count: progressData.liveStats?.Unknown || 0,
                            skipped_count: progressData.liveStats?.Skipped || 0,
                            syntax_error_count: progressData.liveStats?.['Syntax Error'] || 0,
                            disposable_count: progressData.liveStats?.Disposable || 0,
                            free_account_count: progressData.liveStats?.['Free Account'] || 0,
                            role_account_count: progressData.liveStats?.['Role-Based'] || 0,
                        }
                        : j
                ));

                // Fetch latest 10 items for live preview
                if (progressData.processed > 0) {
                    try {
                        const itemsRes = await api.get(`/verify/jobs/${jobId}/items?limit=10&offset=0`);
                        if (itemsRes.data && itemsRes.data.success) {
                            setPreviewItems(itemsRes.data.data.items || []);
                            setPreviewJobId(jobId);
                        }
                    } catch (err) { }
                }

                // Check for completion or termination
                const isFinished = ['completed', 'failed', 'cancelled'].includes(progressData.status);
                if (isFinished) {
                    clearInterval(interval);
                    setIsBulkVerifying(false);
                    refreshJobs(false);
                    return;
                }

                // Frequency adjustment
                const newFreq = getPollingFrequency(progressData.total || 0);
                if (currentFrequency !== newFreq) {
                    clearInterval(interval);
                    currentFrequency = newFreq;
                    interval = setInterval(pollStats, newFreq);
                }
            } catch (error) {
                if (error.response?.status === 404) {
                    clearInterval(interval);
                    setIsBulkVerifying(false);
                }
            }
        };

        let currentFrequency = 2000;
        let interval = setInterval(pollStats, currentFrequency);
        return () => clearInterval(interval);
    }, [jobId, isBulkVerifying, jobs]);

    // useEffect to update jobPage when pendingPage changes
    useEffect(() => {
        if (pendingPage !== null && pendingPage !== jobPage) {
            setJobPage(pendingPage);
        }
    }, [pendingPage, jobPage]);

    // useLayoutEffect to scroll/highlight after pagination updates
    useLayoutEffect(() => {
        if (selectedJobToHighlight) {
            let tries = 0;
            const maxTries = 10;
            function tryScroll() {
                const el = document.getElementById(`job-card-${selectedJobToHighlight}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('ring-2', 'ring-indigo-400');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-400'), 2000);
                    setSelectedJobToHighlight(null);
                    setPendingPage(null);
                } else if (tries < maxTries) {
                    tries++;
                    window.requestAnimationFrame(tryScroll);
                } else {
                    setSelectedJobToHighlight(null);
                    setPendingPage(null);
                }
            }
            tryScroll();
        }
    }, [jobPage, jobs, selectedJobToHighlight]);

    const displayJobs = jobs.filter(j => !deletingJobIds.has(j.job_id) && j.status !== 'deleting');

    // Add confirmCancelJob function
    const confirmCancelJob = async () => {
        if (!jobToCancel) return;
        setIsCancellingJob(true);
        await runAction(async () => {
            const toastId = toast.loading('Cancelling job...');
            try {
                await apiService.cancelJob(jobToCancel);
                toast.success('Job cancelled');
                setCancelModalOpen(false);
                setJobToCancel(null);
                await refreshJobs();
            } catch (error) {
                // If the job is already cancelled (404) or not cancellable (400), treat as success
                if (error?.response?.status === 404 || error?.response?.status === 400) {
                    toast.success('Job already cancelled');
                    setCancelModalOpen(false);
                    setJobToCancel(null);
                    await refreshJobs();
                } else {
                    toast.error('Failed to cancel job');
                }
            } finally {
                toast.dismiss(toastId);
            }
        }, { message: 'Cancelling job...' })
        setIsCancellingJob(false);
    };

    // useEffect to focus and highlight job after jobs update
    useEffect(() => {
        if (jobToFocusAfterRefresh && jobs.length > 0) {
            const newSortedJobs = [...jobs].sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
            const newIdx = newSortedJobs.findIndex(j => j.job_id === jobToFocusAfterRefresh);
            if (newIdx !== -1) {
                const newPage = Math.floor(newIdx / jobsPerPage) + 1;
                setPendingPage(newPage);
                setSelectedJobToHighlight(jobToFocusAfterRefresh);
            }
            setJobToFocusAfterRefresh(null);
        }
    }, [jobs, jobToFocusAfterRefresh]);

    // Periodically refresh jobs list with smart polling during deletions
    useEffect(() => {
        let refreshInterval;

        const startPolling = () => {
            // Use less frequent polling during deletions but don't skip completely
            const interval = isDeletingAny ? 12000 : 8000; // 12s during deletions, 8s normally

            refreshInterval = setInterval(() => {
                // Only skip refresh during single job deletion for first 5 seconds
                if (isDeletingAny && deletingJobId && deletingJobIds.size <= 1) {
                    const now = Date.now();
                    if (window.lastDeleteStartTime && (now - window.lastDeleteStartTime) < 5000) {
                        // Skipping refresh logging removed for production
                        return;
                    }
                }

                // Refresh jobs list
                refreshJobs(false, true);
            }, interval);
        };

        startPolling();

        // Restart polling when deletion state changes
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [isDeletingAny, deletingJobIds.size, refreshJobs]); // Restart polling when deletion state changes or filters/page change

    // Optimized effect for cleaning up deletingJobIds
    useEffect(() => {
        if (deletingJobIds.size > 0) {
            setDeletingJobIds(prev => {
                const newSet = new Set();
                // Only keep IDs that still exist in jobs list or are still being deleted
                prev.forEach(jobId => {
                    const jobExists = jobs.some(j => j.job_id === jobId);
                    const isActivelyDeleting = isDeletingAny && (deletingJobId === jobId);
                    if (jobExists || isActivelyDeleting) {
                        newSet.add(jobId);
                    }
                });

                // Only update if the set actually changed
                if (newSet.size !== prev.size || [...newSet].some(id => !prev.has(id))) {
                    return newSet;
                }
                return prev;
            });
        }
    }, [jobs, isDeletingAny, deletingJobId]);

    return (
        <div className="space-y-8 pb-12">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">List Verification</h1>
                <p className="mt-1 text-slate-500 dark:text-slate-400 font-medium">
                    Upload and verify email lists in bulk with enterprise-grade accuracy
                </p>
            </div>

            {/* Dark mode CSS variables for react-select */}
            <style>{`
        html.dark { 
          --lv-control-bg: #111827; 
          --lv-control-border: #374151; 
          --lv-control-text: #e5e7eb; 
          --lv-placeholder: #9ca3af; 
          --lv-menu-bg: #111827; 
          --lv-menu-text: #e5e7eb; 
          --lv-option-bg: #111827; 
          --lv-option-hover: #1f2937; 
          --lv-option-selected: #374151; 
          --lv-option-text: #e5e7eb; 
          --lv-indicator: #9ca3af; 
        }
      `}</style>

            {/* Settings warning */}
            {!settingsCheckLoading && !settingsCheck?.scannerConfigured && (
                <div className="glass border-l-4 border-warning-400 dark:border-warning-500 p-6 rounded-2xl animate-slide-up">
                    <div className="flex items-start gap-4">
                        <div className="h-12 w-12 bg-warning-100 dark:bg-warning-900/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Configuration Required</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                                Email scanner settings are not configured. Please configure your SMTP and IMAP settings to enable bulk verification.
                            </p>
                            <Link to="/settings" className="btn btn-secondary shadow-sm"><Settings className="h-4 w-4 mr-2" />Configure Settings</Link>
                        </div>
                    </div>
                </div>
            )}

            {/* No Active Nodes Warning */}
            {!isNodesCheckLoading && settingsCheck?.scannerConfigured && onlineNodesCount === 0 && (
                <div className="glass border-l-4 border-rose-400 dark:border-rose-500 p-6 rounded-2xl animate-slide-up">
                    <div className="flex items-start gap-4">
                        <div className="h-12 w-12 bg-rose-100 dark:bg-rose-900/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <AlertCircle className="h-6 w-6 text-rose-600 dark:text-rose-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">No Active Processing Nodes</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                                All verification nodes are currently offline or paused. Verification jobs will stay in "pending" status until at least one node is active.
                            </p>
                            <div className="flex gap-3">
                                <Link to="/settings" className="btn btn-secondary shadow-sm">
                                    <Settings className="h-4 w-4 mr-2" />
                                    Activate Nodes
                                </Link>
                                <a href="https://docs.softlom.com/satellite-node" target="_blank" rel="noreferrer" className="btn btn-ghost text-sm">
                                    <InfoIcon className="h-4 w-4 mr-2" />
                                    How to start a Satellite?
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Upload Modal */}
            {uploadModalOpen && settingsCheck?.scannerConfigured && (
                <Portal><div className="modal-overlay" onClick={() => { if (!isUploadingQueue) setUploadModalOpen(false) }}>
                    <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                                <div className="h-12 w-12 bg-brand-100 dark:bg-brand-900/30 rounded-2xl flex items-center justify-center">
                                    <Upload className="h-6 w-6 text-brand-600" />
                                </div>
                                Import New List{uploadQueue.length > 1 ? 's' : ''}
                            </h3>
                            {!isUploadingQueue && (
                                <button
                                    onClick={() => { setUploadModalOpen(false); setUploadQueue([]); uploadQueueRef.current = []; setUploadGroupName('') }}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                            Upload one or more CSV files. Each file becomes a separate verification job.
                            Max <span className="text-brand-600 font-bold">{maxUploadSizeMB}MB</span> per file.
                        </p>

                        {/* Dropzone */}
                        <div
                            {...getRootProps()}
                            className={`relative border-2 border-dashed rounded-[2rem] p-8 text-center transition-all duration-300 group ${isDragActive
                                ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-500/5'
                                : 'border-slate-200 dark:border-slate-800 hover:border-brand-400 hover:bg-slate-50/50 dark:hover:bg-slate-800/50'
                                } cursor-pointer`}
                        >
                            <input {...getInputProps()} />
                            <div className="h-14 w-14 bg-white dark:bg-slate-800 rounded-2xl shadow-premium flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                                <Upload className="h-7 w-7 text-brand-500" />
                            </div>
                            <p className="text-slate-900 dark:text-white font-bold text-base">
                                {isDragActive ? 'Drop your files here' : 'Click to select or drag & drop'}
                            </p>
                            <p className="mt-1 text-slate-500 dark:text-slate-400 text-sm font-medium">
                                Multiple CSV files supported — each becomes its own job
                            </p>
                        </div>

                        {/* Default group for newly dropped files */}
                        <div className="mt-5 mb-2">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                    <Tag className="h-4 w-4 text-brand-500" /> Group / Category (Optional)
                                </label>
                                {uploadQueue.length > 0 && !isUploadingQueue && (
                                    <button
                                        onClick={() => {
                                            setUploadQueue(prev => {
                                                const updated = prev.map(i =>
                                                    i.status === 'pending' ? { ...i, group: uploadGroupName } : i
                                                )
                                                uploadQueueRef.current = updated
                                                return updated
                                            })
                                        }}
                                        className="text-xs font-bold text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-200 transition-colors underline-offset-2 hover:underline"
                                    >
                                        Apply to All Files
                                    </button>
                                )}
                            </div>
                            <CreatableSelect
                                isClearable
                                placeholder="Select or type a group name..."
                                options={groups.map(g => ({ value: g.name || g, label: `${g.name || g} (${g.count || '?'} lists)` }))}
                                onChange={(v) => setUploadGroupName(v ? v.value : '')}
                                value={uploadGroupName ? { value: uploadGroupName, label: uploadGroupName } : null}
                                styles={customSelectStyles}
                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                            />
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                Set a group then click <span className="font-semibold text-brand-500">Apply to All Files</span> to assign it to every file at once, or set per-file below.
                            </p>
                        </div>

                        {/* File Queue */}
                        {uploadQueue.length > 0 && (
                            <div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        {uploadQueue.length} file{uploadQueue.length !== 1 ? 's' : ''} queued
                                    </span>
                                    {!isUploadingQueue && (
                                        <button
                                            onClick={() => { setUploadQueue([]); uploadQueueRef.current = [] }}
                                            className="text-xs text-danger-500 hover:underline font-semibold"
                                        >
                                            Clear All
                                        </button>
                                    )}
                                </div>
                                {uploadQueue.map(item => (
                                    <div key={item.id} className="glass rounded-xl p-3 border border-slate-200 dark:border-slate-800 animate-fade-in">
                                        <div className="flex items-start gap-3">
                                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                item.status === 'done' ? 'bg-green-100 dark:bg-green-900/30' :
                                                item.status === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                                                item.status === 'cancelled' ? 'bg-slate-100 dark:bg-slate-800' :
                                                'bg-brand-100 dark:bg-brand-900/30'
                                            }`}>
                                                {item.status === 'done' ? <CheckCircle className="h-5 w-5 text-green-600" /> :
                                                 item.status === 'error' ? <AlertCircle className="h-5 w-5 text-red-500" /> :
                                                 item.status === 'uploading' ? <Loader2 className="h-5 w-5 text-brand-600 animate-spin" /> :
                                                 item.status === 'cancelled' ? <span className="text-slate-400 text-xs font-bold">✕</span> :
                                                 <FileText className="h-5 w-5 text-brand-600" />}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate" title={item.file.name}>
                                                    {item.file.name}
                                                </p>
                                                <p className="text-xs text-slate-400 mb-1">
                                                    {(item.file.size / 1024 / 1024).toFixed(2)} MB
                                                    {item.status === 'done' && <span className="ml-2 text-green-600 font-bold">✓ Uploaded</span>}
                                                    {item.status === 'cancelled' && <span className="ml-2 text-slate-400">Cancelled</span>}
                                                    {item.status === 'error' && <span className="ml-2 text-red-500">{item.error}</span>}
                                                </p>

                                                {/* Per-file group (editable only when pending & not running) */}
                                                {item.status === 'pending' && !isUploadingQueue && (
                                                    <CreatableSelect
                                                        isClearable
                                                        placeholder="Group (optional)..."
                                                        options={groups.map(g => ({ value: g.name || g, label: `${g.name || g} (${g.count || '?'} lists)` }))}
                                                        onChange={(v) => {
                                                            const grp = v ? v.value : ''
                                                            updateQueueItem(item.id, { group: grp })
                                                            uploadQueueRef.current = uploadQueueRef.current.map(i =>
                                                                i.id === item.id ? { ...i, group: grp } : i
                                                            )
                                                        }}
                                                        value={item.group ? { value: item.group, label: item.group } : null}
                                                        styles={{
                                                            ...customSelectStyles,
                                                            control: (base, state) => ({
                                                                ...customSelectStyles.control(base, state),
                                                                minHeight: '2rem',
                                                                fontSize: '0.75rem'
                                                            })
                                                        }}
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        menuPosition="fixed"
                                                    />
                                                )}
                                                {(item.status !== 'pending' || isUploadingQueue) && item.group && (
                                                    <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                                                        <Tag className="h-3 w-3" /> {item.group}
                                                    </p>
                                                )}

                                                {/* Progress bar */}
                                                {(item.status === 'uploading' || item.status === 'done') && (
                                                    <div className="mt-2">
                                                        <div className="flex justify-between text-xs font-semibold mb-0.5">
                                                            <span className="text-slate-500">{item.status === 'done' ? 'Complete' : 'Uploading...'}</span>
                                                            <span className="text-brand-600">{item.progress}%</span>
                                                        </div>
                                                        <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full transition-all duration-300 ${item.status === 'done' ? 'bg-green-500' : 'bg-gradient-to-r from-brand-600 to-primary-600'}`}
                                                                style={{ width: `${item.progress}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Per-item actions */}
                                            <div className="flex-shrink-0">
                                                {item.status === 'pending' && !isUploadingQueue && (
                                                    <button
                                                        onClick={() => {
                                                            setUploadQueue(prev => {
                                                                const updated = prev.filter(i => i.id !== item.id)
                                                                uploadQueueRef.current = updated
                                                                return updated
                                                            })
                                                        }}
                                                        className="text-slate-400 hover:text-danger-500 transition-colors"
                                                        title="Remove file"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {item.status === 'uploading' && (
                                                    <button
                                                        onClick={() => handleCancelUpload(item.id)}
                                                        className="text-danger-500 hover:text-danger-700 text-xs font-bold transition-colors"
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload All button */}
                        {uploadQueue.some(i => i.status === 'pending') && (
                            <div className="mt-5 flex items-center justify-between gap-3">
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {uploadQueue.filter(i => i.status === 'pending').length} file(s) ready
                                </span>
                                <div className="flex items-center gap-3">
                                    {isUploadingQueue && (
                                        <button onClick={() => handleCancelUpload(null)} className="text-danger-500 text-sm font-bold hover:underline">
                                            Cancel All
                                        </button>
                                    )}
                                    <button
                                        onClick={handleBulkVerification}
                                        disabled={isUploadingQueue}
                                        className="btn btn-primary shadow-brand-500/20"
                                    >
                                        {isUploadingQueue
                                            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Uploading...</>
                                            : <><Upload className="h-4 w-4 mr-2" />Upload {uploadQueue.filter(i => i.status === 'pending').length > 1 ? `All ${uploadQueue.filter(i => i.status === 'pending').length} Files` : 'File'}</>
                                        }
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* All done summary */}
                        {uploadQueue.length > 0 && uploadQueue.every(i => ['done', 'error', 'cancelled'].includes(i.status)) && (
                            <div className="mt-5 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                    <span className="text-green-600">{uploadQueue.filter(i => i.status === 'done').length} uploaded</span>
                                    {uploadQueue.filter(i => i.status === 'error').length > 0 && (
                                        <span className="text-red-500">, {uploadQueue.filter(i => i.status === 'error').length} failed</span>
                                    )}
                                    {uploadQueue.filter(i => i.status === 'cancelled').length > 0 && (
                                        <span className="text-slate-400">, {uploadQueue.filter(i => i.status === 'cancelled').length} cancelled</span>
                                    )}
                                </div>
                                <button
                                    onClick={() => { setUploadModalOpen(false); setUploadQueue([]); uploadQueueRef.current = []; setUploadGroupName('') }}
                                    className="btn btn-secondary text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </div></Portal>
            )}

            {/* Only show upload section if scanner settings are configured */}
            {/* Show spinner in upload area if settingsCheckLoading is true */}
            {settingsCheckLoading ? (
                <div className="card">
                    <div className="card-header">
                        <h3 className="text-lg font-medium text-gray-900">Bulk Email Verification</h3>
                    </div>
                    <div className="card-body flex justify-center items-center min-h-[120px]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                        <span className="ml-3 text-blue-700 font-medium">Loading settings...</span>
                    </div>
                </div>
            ) : null}

            {/* Jobs List */}
            <div className="card">
                <div className="card-header">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setUploadModalOpen(true)}
                                disabled={settingsCheckLoading || !settingsCheck?.scannerConfigured}
                                className="btn btn-primary shadow-brand-500/20"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                Upload List
                            </button>
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={selectAll && jobs.length > 0}
                                    onChange={handleSelectAll}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                Select All
                            </label>
                            <h3 className="text-base font-medium text-gray-900">All Jobs</h3>
                            {selectedJobs.size > 0 && (
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-600">
                                        {selectedJobs.size} selected
                                    </span>
                                    <button
                                        onClick={handleBulkPause}
                                        disabled={bulkActionLoading !== null || isDeletingAny}
                                        className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                            bulkActionLoading === 'pause'
                                                ? 'bg-orange-400 text-white cursor-not-allowed'
                                                : 'bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400'
                                        }`}
                                    >
                                        {bulkActionLoading === 'pause' ? (
                                            <>
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                Pausing...
                                            </>
                                        ) : (
                                            <>
                                                <Pause className="h-3 w-3 mr-1" />
                                                Pause
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleBulkStart}
                                        disabled={bulkActionLoading !== null || isDeletingAny}
                                        className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                            bulkActionLoading === 'start'
                                                ? 'bg-green-400 text-white cursor-not-allowed'
                                                : 'bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400'
                                        }`}
                                    >
                                        {bulkActionLoading === 'start' ? (
                                            <>
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                Starting...
                                            </>
                                        ) : (
                                            <>
                                                <Play className="h-3 w-3 mr-1" />
                                                Start
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={bulkActionLoading !== null || isDeletingAny}
                                        className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md transition-colors ${isDeletingAny
                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                            }`}
                                    >
                                        {isDeletingAny ? (
                                            <>
                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                                Deleting...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 className="h-3 w-3 mr-1" />
                                                Delete
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {!isLoadingConcurrency && (
                            <div className="flex items-center space-x-2 text-xs">
                                <span className="text-gray-600">Concurrency:</span>
                                <span className={`font-medium ${jobs.filter(j =>
                                    j.status === 'processing' &&
                                    j.total_emails > 0 &&
                                    j.processed_emails < j.total_emails
                                ).length >= workerConcurrency
                                    ? 'text-orange-600'
                                    : 'text-green-600'
                                    }`}>
                                    {jobs.filter(j =>
                                        j.status === 'processing' &&
                                        j.total_emails > 0 &&
                                        j.processed_emails < j.total_emails
                                    ).length} / {workerConcurrency}
                                </span>
                                <span className="text-gray-500">processing jobs</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500">
                                    {jobs.filter(j => j.status === 'pending').length} pending
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {/* Search and Filter Controls */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-350 mb-2 flex items-center gap-2">
                                <Folder className="h-4.5 w-4.5 text-blue-500" /> Search or Select a List
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by list name or ID..."
                                    value={jobSearchTerm}
                                    onChange={e => {
                                        setJobSearchTerm(e.target.value);
                                        setJobPage(1);
                                    }}
                                    className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                />
                                {jobSearchTerm && (
                                    <button
                                        onClick={() => setJobSearchTerm('')}
                                        className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                )}
                            </div>
                            {jobsTotal != null && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {jobsTotal.toLocaleString()} total list{jobsTotal !== 1 ? 's' : ''}{jobSearchTerm ? ` matching "${jobSearchTerm}"` : ''}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-350 mb-2 flex items-center gap-2">
                                <Tag className="h-4.5 w-4.5 text-brand-500" /> Filter by Category / Group
                            </label>
                            <Select
                                classNamePrefix="react-select"
                                options={[
                                    { value: 'all', label: '📁 All Groups' },
                                    { value: '__none__', label: `🚫 No Group (${ungroupedCount})` },
                                    ...groups.map(g => ({ value: g.name || g, label: `🏷️ ${g.name || g} (${g.count || '?'})` }))
                                ]}
                                placeholder="Filter by group..."
                                value={selectedGroupFilter ? { value: selectedGroupFilter, label: selectedGroupFilter === 'all' ? '📁 All Groups' : selectedGroupFilter === '__none__' ? `🚫 No Group (${ungroupedCount})` : `🏷️ ${selectedGroupFilter}` } : { value: 'all', label: '📁 All Groups' }}
                                onChange={option => {
                                    setSelectedGroupFilter(option ? option.value : 'all');
                                    setJobPage(1); // Reset to page 1 when filtering
                                }}
                                styles={customSelectStyles}
                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                            />
                        </div>
                    </div>

                    {/* Status Filter Pills */}
                    <div className="flex flex-wrap items-center gap-2 mb-6">
                        {[
                            { value: 'all',         label: 'All',              emoji: '📋' },
                            { value: 'processing',  label: 'Processing',       emoji: '⚡' },
                            { value: 'queued',      label: 'Queued',           emoji: '⏳' },
                            { value: 'ready,pending', label: 'Ready to Verify', emoji: '✅' },
                            { value: 'paused',      label: 'Paused',           emoji: '⏸️' },
                            { value: 'uploading',   label: 'Uploading',        emoji: '📤' },
                            { value: 'completed',   label: 'Completed',        emoji: '🏁' },
                            { value: 'failed',      label: 'Failed',           emoji: '❌' },
                            { value: 'cancelled',   label: 'Cancelled',        emoji: '🚫' },
                        ].map(pill => (
                            <button
                                key={pill.value}
                                onClick={() => { setSelectedStatusFilter(pill.value); setJobPage(1); }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150 ${
                                    selectedStatusFilter === pill.value
                                        ? 'bg-brand-600 text-white border-brand-600 shadow-sm shadow-brand-500/30'
                                        : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400'
                                }`}
                            >
                                <span>{pill.emoji}</span>
                                {pill.label}
                            </button>
                        ))}
                        {selectedStatusFilter !== 'all' && (
                            <button
                                onClick={() => { setSelectedStatusFilter('all'); setJobPage(1); }}
                                className="text-xs text-slate-400 hover:text-danger-500 font-semibold ml-1 transition-colors"
                            >
                                Clear filter ✕
                            </button>
                        )}
                    </div>
                    {jobsLoading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 py-6">
                            {[...Array(jobsPerPage)].map((_, idx) => (
                                <div key={idx} className="relative bg-white dark:bg-gray-900 rounded-xl shadow p-3 flex flex-col justify-between border border-gray-200 dark:border-gray-800 animate-pulse">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-2">
                                            <div className="h-4 w-4 bg-gray-200 dark:bg-gray-800 rounded" />
                                            <div className="h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded" />
                                        </div>
                                        <div className="h-4 w-16 bg-gray-200 dark:bg-gray-800 rounded" />
                                    </div>
                                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-800 rounded mb-4" />
                                    <div className="h-3 w-full bg-gray-200 dark:bg-gray-800 rounded-full mb-4" />
                                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                        <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded" />
                                    </div>
                                    <div className="flex space-x-2 mt-auto">
                                        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-800 rounded" />
                                        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-800 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : displayJobs.length > 0 ? (
                        <>
                            {/* Clear filters quick-buttons */}
                            {(jobSearchTerm || (selectedGroupFilter && selectedGroupFilter !== 'all')) && (
                                <div className="max-w-4xl mx-auto mb-4 flex gap-3">
                                    {jobSearchTerm && (
                                        <button
                                            onClick={() => setJobSearchTerm('')}
                                            className="px-3 py-1 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                                        >
                                            &times; Clear Search
                                        </button>
                                    )}
                                    {selectedGroupFilter && selectedGroupFilter !== 'all' && (
                                        <button
                                            onClick={() => setSelectedGroupFilter('all')}
                                            className="px-3 py-1 text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 rounded-lg border border-brand-200 transition-colors"
                                        >
                                            &times; Clear Group Filter ({selectedGroupFilter === '__none__' ? 'No Group' : selectedGroupFilter})
                                        </button>
                                    )}
                                </div>
                            )}
                            {/* Active Verifications Section — only on "All" filter, processing jobs only */}
                            {selectedStatusFilter === 'all' && jobs.some(j => j.status === 'processing' && j.processed_emails < j.total_emails) && (
                                <div className="mb-6 animate-fade-in">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex h-2 w-2 relative">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500"></span>
                                        </div>
                                        <h4 className="text-sm font-bold text-brand-600 dark:text-brand-400 uppercase tracking-wider">Active Verifications</h4>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {jobs.filter(j => j.status === 'processing' && j.processed_emails < j.total_emails).map(job => (
                                            <div
                                                key={`active-${job.job_id}`}
                                                onClick={() => {
                                                    setSelectedJobToHighlight(job.job_id);
                                                }}
                                                className="glass border border-brand-200 dark:border-brand-900/50 px-4 py-2 rounded-xl flex items-center gap-3 cursor-pointer hover:bg-brand-50/50 dark:hover:bg-brand-900/10 transition-all group"
                                            >
                                                <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-slate-900 dark:text-white truncate max-w-[150px]">{job.job_name}</p>
                                                    <p className="text-[10px] text-slate-500 font-medium">Click to view details</p>
                                                </div>
                                                <div className="h-1.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex-shrink-0">
                                                    <div
                                                        className="h-full bg-brand-500 transition-all duration-1000"
                                                        style={{ width: `${Math.round((job.processed_emails / (job.total_emails || 1)) * 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {displayJobs.map((job) => (
                                    <div key={job.job_id} id={`job-card-${job.job_id}`} className={`relative bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-5 flex flex-col justify-between border border-gray-200 dark:border-gray-800 hover:shadow-xl transition-all duration-300 ${job.status === 'processing' && job.processed_emails < job.total_emails ? 'ring-2 ring-brand-500 shadow-brand-100 dark:shadow-brand-900/20' : ''} ${job.status === 'deleting' ? 'opacity-60 grayscale pointer-events-none' : ''}`}>
                                        {/* Deleting state */}
                                        {job.status === 'deleting' && (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-white dark:bg-gray-900 bg-opacity-80 rounded-xl">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                                    <span className="font-semibold text-gray-500">Deleting...</span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-2">
                                            <div className="flex items-center space-x-2 min-w-0 max-w-full">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedJobs.has(job.job_id)}
                                                    onChange={() => handleSelectJob(job.job_id)}
                                                    disabled={isDeletingAny}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <span
                                                    className="font-bold text-slate-900 dark:text-white truncate text-base block mb-1 flex-1"
                                                    data-tip={job.job_name}
                                                    data-for={`job-name-tooltip-${job.job_id}`}
                                                >
                                                    {job.job_name}
                                                </span>
                                                <ReactTooltip
                                                    id={`job-name-tooltip-${job.job_id}`}
                                                    place="top"
                                                    effect="solid"
                                                    className="!max-w-xs !text-xs !rounded !shadow-lg !bg-gray-900 !text-white"
                                                />
                                            </div>
                                            {/* Hide status badge for uploading jobs */}
                                            {!(job.status === 'uploading' || job.status === 'uploading_paused') && (
                                                <span className={`ml-2 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${(job.status === 'completed' || (job.status === 'processing' && job.processed_emails >= job.total_emails && job.total_emails > 0 && !job.is_greylisting_wait)) ? 'bg-green-100 text-green-700' :
                                                    job.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                                        job.status === 'queued' ? 'bg-purple-100 text-purple-700' :
                                                        job.status === 'ready' || job.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                                job.status === 'paused' ? 'bg-orange-100 text-orange-700' :
                                                                    job.status === 'failed' ? 'bg-red-100 text-red-700' :
                                                                        'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {getStatusText(job)}
                                                </span>
                                            )}

                                            {/* Verification percentage badge - show at top for processing jobs */}
                                            {job.status === 'processing' && getJobStats(job).total > 0 && (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                                                    {getJobStats(job).processed < getJobStats(job).total && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                                                    {Math.round((Math.min(getJobStats(job).processed, getJobStats(job).total) / getJobStats(job).total) * 100)}%
                                                    {getJobStats(job).processed < getJobStats(job).total ? ' verifying' : ' verified'}
                                                </span>
                                            )}

                                            {/* Completion percentage badge for completed jobs */}
                                            {job.status === 'completed' && (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                                                    100% complete
                                                </span>
                                            )}

                                            {/* Progress/Uploading badge */}
                                            {(job.upload_status === 'processing') ? (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                    {job.total_emails > 0 ? `${Math.round((job.processed_emails / job.total_emails) * 100)}% ` : ''}
                                                    Uploading...
                                                </span>
                                            ) : job.upload_status === 'pending' ? (
                                                <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200 flex items-center gap-1">
                                                    <Pause className="h-3 w-3 mr-1" />
                                                    Queued
                                                </span>
                                            ) : (
                                                shouldShow100PercentDone(job) && job.status !== 'processing' && job.status !== 'completed' && (
                                                    <span className="ml-3 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                                        100% done
                                                    </span>
                                                )
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-300 mb-1">Created: {new Date(job.created_at).toLocaleString()}</div>
                                        <div className="flex flex-wrap items-center gap-2 mb-3">
                                            <span className="text-[11px] font-bold text-gray-400 bg-gray-100 dark:bg-gray-800 dark:text-gray-500 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-800 font-mono select-all shrink-0" title="Job ID (click to select)">ID: {job.job_id}</span>
                                            
                                            {/* Group / Category Tag */}
                                            {job.group_name ? (
                                                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                                                    <Tag className="h-3 w-3 text-blue-500" />
                                                    {job.group_name}
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            startEditingGroup(job);
                                                        }}
                                                        className="hover:text-blue-900 ml-0.5 text-blue-400 transition-colors"
                                                        title="Edit Category"
                                                    >
                                                        ✎
                                                    </button>
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        startEditingGroup(job);
                                                    }}
                                                    className="inline-flex items-center gap-1 text-[11px] font-semibold bg-gray-50 hover:bg-gray-100 text-gray-500 dark:bg-gray-850 dark:hover:bg-gray-800 dark:text-gray-400 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 transition-colors"
                                                    title="Assign Category"
                                                >
                                                    <Tag className="h-2.5 w-2.5 text-gray-400" />
                                                    + Category
                                                </button>
                                            )}
                                        </div>
                                        {/* Progress bar or uploading animation */}
                                        {shouldShowProgress(job) && (
                                            <div className="mb-4">
                                                <div className="w-full bg-gray-200 dark:bg-gray-800 rounded-full h-3">
                                                    <div
                                                        className={`h-3 rounded-full transition-all duration-500 ${job.status === 'paused' ? 'bg-orange-500' :
                                                            job.status === 'processing' ? 'bg-blue-500 animate-pulse' :
                                                            job.status === 'queued' ? 'bg-purple-400 animate-pulse' : 'bg-yellow-500'
                                                            }`}
                                                        style={{ width: `${Math.min(100, Math.round((getJobStats(job).processed / (getJobStats(job).total || 1)) * 100))}%` }}
                                                    ></div>
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-300 mt-2 flex flex-wrap items-center justify-end gap-x-1.5 gap-y-1">
                                                    {job.status === 'processing' ? (
                                                        <>
                                                            <span className="whitespace-nowrap">
                                                                {Math.min(getJobStats(job).processed, getJobStats(job).total)} / {getJobStats(job).total} verified
                                                            </span>
                                                            {job.is_greylisting_wait ? (
                                                                <span className="inline-flex items-center text-orange-500 font-medium whitespace-nowrap">
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-1 flex-shrink-0" />
                                                                    Bounce monitoring in progress (can take 1-2 hrs)
                                                                </span>
                                                            ) : progress?.is_greylisting_wait ? (
                                                                <span className="inline-flex items-center text-orange-500 font-medium whitespace-nowrap">
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-1 flex-shrink-0" />
                                                                    Waiting for IMAP bounce results...
                                                                </span>
                                                            ) : getJobStats(job).processed < getJobStats(job).total && (
                                                                <span className="inline-flex items-center whitespace-nowrap">
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-1 flex-shrink-0" />
                                                                    Verifying...
                                                                </span>
                                                            )}
                                                            {/* Stuck indicator: no progress for 10+ min */}
                                                            {progress?.stale && job.job_id === jobId && (
                                                                <span className="inline-flex items-center text-amber-600 dark:text-amber-400 font-medium whitespace-nowrap">
                                                                    <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                                                                    Stuck? {(progress.stuckRemaining || 0).toLocaleString()} remaining — use Force Complete
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : job.status === 'completed' ? (
                                                        <>
                                                            <span>{getJobStats(job).total} / {getJobStats(job).total} verified</span>
                                                        </>
                                                    ) : job.status === 'ready' || job.status === 'pending' ? (
                                                        <>
                                                            <span>{getJobStats(job).total} emails ready</span>
                                                        </>
                                                    ) : job.status === 'queued' ? (
                                                        <span className="inline-flex items-center text-purple-600 dark:text-purple-400 font-medium whitespace-nowrap">
                                                            <Loader2 className="h-3 w-3 animate-spin mr-1 flex-shrink-0" />
                                                            Queued — waiting for a free worker slot...
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <span>{Math.min(getJobStats(job).processed, getJobStats(job).total)} / {getJobStats(job).total} processed</span>
                                                            {job.status === 'paused' && <span>(Paused)</span>}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {job.upload_status === 'pending' && (
                                            <div className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                                <Pause className="h-5 w-5" />
                                                <span className="font-medium">Waiting for upload slot...</span>
                                            </div>
                                        )}
                                        {job.upload_status === 'processing' && (
                                            <div className="mb-4 flex items-center gap-2 text-blue-600 animate-pulse">
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <span className="font-medium">Uploading and processing CSV... Please wait</span>
                                            </div>
                                        )}
                                        {/* Results summary for completed jobs and all jobs */}
                                        <div className="mb-6">
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                                <div className="flex justify-between items-center p-2 rounded bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                                    <span className="text-slate-500 dark:text-slate-400 font-medium text-xs">Total:</span>
                                                    <span className="font-bold text-slate-900 dark:text-white">{job.total_emails}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 rounded bg-green-50/50 dark:bg-green-900/10 border border-green-100/50 dark:border-green-900/20">
                                                    <span className="text-green-600 dark:text-green-400 font-medium text-xs">Valid:</span>
                                                    <span className="font-bold text-green-700 dark:text-green-300">{getJobStats(job).valid}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 rounded bg-red-50/50 dark:bg-red-900/10 border border-red-100/50 dark:border-red-900/20">
                                                    <span className="text-red-600 dark:text-red-400 font-medium text-xs">Invalid:</span>
                                                    <span className="font-bold text-red-700 dark:text-red-300">{getJobStats(job).invalid}</span>
                                                </div>
                                                <div className="flex justify-between items-center p-2 rounded bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100/50 dark:border-amber-900/20" title="Catch-all emails are considered risky because the server accepts everything, making it impossible to verify the exact mailbox.">
                                                    <span className="text-amber-600 dark:text-amber-400 font-medium text-xs text-nowrap">Catch-all/Risky:</span>
                                                    <span className="font-bold text-amber-700 dark:text-amber-300">{getJobStats(job).catch_all}</span>
                                                </div>

                                                {/* Mini Stats Grid */}
                                                <div className="col-span-2 mt-1 grid grid-cols-4 gap-2">
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Rate Ltd</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).rate_limited}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Unknown</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).unknown}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-cyan-50/50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-800/50">
                                                        <span className="text-cyan-600 dark:text-cyan-400 text-[9px] uppercase font-bold tracking-tight">IMAP Wait</span>
                                                        <span className="text-cyan-700 dark:text-cyan-300 font-bold text-xs">{getJobStats(job).awaiting_imap}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Syntax</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).syntax_error}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Disposable</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).disposable}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Free Acc</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).free_account}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Role Acc</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).role_account}</span>
                                                    </div>
                                                    <div className="flex flex-col p-1.5 rounded bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
                                                        <span className="text-slate-500 text-[9px] uppercase font-bold tracking-tight">Skipped</span>
                                                        <span className="text-slate-700 dark:text-slate-300 font-bold text-xs">{getJobStats(job).skipped}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Action buttons, only show if not uploading */}
                                        <div className="flex flex-wrap gap-2 mt-auto">
                                            {/* Uploading/Paused: Show Cancel */}
                      {job.status === 'deleting' ? null : (job.status === 'uploading' || job.status === 'uploading_paused') && (
                        <button
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            (deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                          }`}
                          onClick={() => handleDeleteJob(job.job_id)}
                          disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                          title={'Cancel upload'}
                        >
                          {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                              Cancelling...
                            </>
                          ) : (
                            'Cancel'
                          )}
                        </button>
                      )}
                                            {/* Pending/Ready: Start and Delete */}
                                            {job.status === 'deleting' ? null : ['pending', 'ready'].includes(job.status) && (
                                                <>
                                                    <button
                                                        onClick={() => handleStartJob(job.job_id)}
                                                        disabled={startingJobId === job.job_id}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            startingJobId === job.job_id
                                                                ? 'bg-green-400 text-white cursor-not-allowed'
                                                                : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                                        }`}
                                                        title="Start verification (will queue if at capacity)"
                                                    >
                                                        {startingJobId === job.job_id ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" />
                                                                Starting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Play className="h-4 w-4 mr-1" />
                                                                Start
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Queued: waiting for slot — Cancel + Delete */}
                                            {job.status === 'deleting' ? null : job.status === 'queued' && (
                                                <>
                                                    <button
                                                        onClick={() => handleCancelJobById(job.job_id)}
                                                        disabled={isCancellingJob}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            isCancellingJob && jobToCancel === job.job_id
                                                                ? 'bg-red-400 text-red-200 cursor-not-allowed'
                                                                : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                        }`}
                                                        title={'Cancel queued job'}
                                                    >
                                                        {isCancellingJob && jobToCancel === job.job_id ? (
                                                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" /> Cancelling...</>
                                                        ) : (
                                                            'Cancel'
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Trash2 className="h-4 w-4 mr-1" />
                                                                Delete
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Processing: Cancel, Pause, Force-Complete and Results */}
                                            {job.status === 'deleting' ? null : job.status === 'processing' && (
                                                <>
                                                    {getJobStats(job).processed >= getJobStats(job).total && getJobStats(job).total > 0 && (
                                                        <button
                                                            onClick={() => navigate('/results', { state: { jobId: job.job_id } })}
                                                            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                            title={'View results'}
                                                        >
                                                            <FileText className="h-4 w-4 mr-1" />
                                                            View Results
                                                        </button>
                                                    )}
                                                    {/* Force Complete: appears when >= 90% done but stuck (hidden during IMAP greylisting) */}
                                                    {getJobStats(job).processed >= getJobStats(job).total * 0.9 &&
                                                     getJobStats(job).processed < getJobStats(job).total &&
                                                     !progress?.is_greylisting_wait && !job.is_greylisting_wait && (
                                                        <button
                                                            onClick={() => openForceCompleteModal(job.job_id,
                                                                progress?.stuckRemaining || (getJobStats(job).total - getJobStats(job).processed)
                                                            )}
                                                            disabled={forceCompletingJob === job.job_id}
                                                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                                forceCompletingJob === job.job_id
                                                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                                                    : 'bg-amber-500 text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400'
                                                            }`}
                                                            title={progress?.stale
                                                                ? `Stuck for 10+ min — ${(progress.stuckRemaining || 0).toLocaleString()} emails remaining`
                                                                : 'Finalize remaining emails as Unknown and finish job'}
                                                        >
                                                            {forceCompletingJob === job.job_id ? (
                                                                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" /> Force-completing...</>
                                                            ) : (
                                                                <><AlertTriangle className="h-4 w-4 mr-1" /> Force Complete</>
                                                            )}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleCancelJobById(job.job_id)}
                                                        disabled={isCancellingJob}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            isCancellingJob && jobToCancel === job.job_id
                                                                ? 'bg-red-400 text-red-200 cursor-not-allowed'
                                                                : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                        }`}
                                                        title={'Cancel verification'}
                                                    >
                                                        {isCancellingJob && jobToCancel === job.job_id ? (
                                                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" /> Cancelling...</>
                                                        ) : (
                                                            'Cancel'
                                                        )}
                                                    </button>
                                                    {getJobStats(job).processed < getJobStats(job).total && (
                                                        <button
                                                            onClick={() => handlePauseJob(job.job_id)}
                                                            disabled={pausingJobId === job.job_id}
                                                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                                pausingJobId === job.job_id
                                                                    ? 'bg-orange-400 text-orange-100 cursor-not-allowed'
                                                                    : 'bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500'
                                                            }`}
                                                            title={'Pause job'}
                                                        >
                                                            {pausingJobId === job.job_id ? (
                                                                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" /> Pausing...</>
                                                            ) : (
                                                                <><Pause className="h-4 w-4 mr-1" /> Pause</>
                                                            )}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            {/* Paused: Resume and Cancel */}
                                            {job.status === 'deleting' ? null : job.status === 'paused' && (
                                                <>
                                                    <button
                                                        onClick={() => handleResumeJob(job.job_id)}
                                                        disabled={startingJobId === job.job_id}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            startingJobId === job.job_id
                                                                ? 'bg-green-400 text-white cursor-not-allowed'
                                                                : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                                        }`}
                                                        title={'Resume job'}
                                                    >
                                                        {startingJobId === job.job_id ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" />
                                                                Resuming...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Play className="h-4 w-4 mr-1" />
                                                                Resume
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleCancelJobById(job.job_id)}
                                                        disabled={isCancellingJob}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            isCancellingJob && jobToCancel === job.job_id
                                                                ? 'bg-red-400 text-red-200 cursor-not-allowed'
                                                                : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                        }`}
                                                        title={'Cancel verification'}
                                                    >
                                                        {isCancellingJob && jobToCancel === job.job_id ? (
                                                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" /> Cancelling...</>
                                                        ) : (
                                                            'Cancel'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Completed: Download and Delete */}
                                            {job.status === 'deleting' ? null : job.status === 'completed' && (
                                                <>
                                                    <button
                                                        onClick={() => navigate('/results', { state: { jobId: job.job_id } })}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                        title={'View results'}
                                                    >
                                                        <FileText className="h-4 w-4 mr-1" />
                                                        View Results
                                                    </button>
                                                    {/* Retry Unknowns button — only shown when there are Unknown emails */}
                                                    {(job.unknown_count || 0) > 0 && (
                                                        <button
                                                            onClick={() => handleRetryUnknowns(job.job_id)}
                                                            disabled={retryingUnknownsJobIds.has(job.job_id)}
                                                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                                retryingUnknownsJobIds.has(job.job_id)
                                                                    ? 'bg-amber-400 text-white cursor-not-allowed'
                                                                    : 'bg-amber-500 text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400'
                                                            }`}
                                                            title={`Retry ${(job.unknown_count || 0).toLocaleString()} Unknown emails`}
                                                        >
                                                            {retryingUnknownsJobIds.has(job.job_id) ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" />
                                                                    Retrying...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <RefreshCw className="h-4 w-4 mr-1" />
                                                                    Retry Unknowns ({(job.unknown_count || 0).toLocaleString()})
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Failed: View Results + Delete */}
                                            {job.status === 'deleting' ? null : job.status === 'failed' && (
                                                <>
                                                    <button
                                                        onClick={() => navigate('/results', { state: { jobId: job.job_id } })}
                                                        className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                        title={'View results'}
                                                    >
                                                        <FileText className="h-4 w-4 mr-1" />
                                                        View Results
                                                    </button>
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                            {/* Cancelled: Verify Again + Delete */}
                                            {job.status === 'deleting' ? null : job.status === 'cancelled' && (
                                                <>
                                                    <button
                                                        onClick={() => handleStartJob(job.job_id)}
                                                        disabled={startingJobId === job.job_id}
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            startingJobId === job.job_id
                                                                ? 'bg-green-400 text-white cursor-not-allowed'
                                                                : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                                        }`}
                                                        title="Re-verify all emails in this job"
                                                    >
                                                        {startingJobId === job.job_id ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1" />
                                                                Starting...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <RefreshCw className="h-4 w-4 mr-1" />
                                                                Verify Again
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${(deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny)
                                                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                                            : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                                                            }`}
                                                        onClick={() => handleDeleteJob(job.job_id)}
                                                        disabled={deletingJobId === job.job_id || deletingJobIds.has(job.job_id) || isDeletingAny}
                                                        title={'Delete job'}
                                                    >
                                                        {(deletingJobId === job.job_id || deletingJobIds.has(job.job_id)) ? (
                                                            <>
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                                                                Deleting...
                                                            </>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 px-2">
                                <SmartPagination
                                    showPageSize={false}
                                    currentPage={Math.min(jobPage, totalJobPages)}
                                    totalPages={totalJobPages}
                                    totalRows={jobsTotal}
                                    pageSize={jobsPerPage}
                                    onPageChange={setJobPage}
                                    disabled={jobsLoading}
                                    className="!pt-0 !border-t-0"
                                />
                            </div>
                        </>
                    ) : (
                        <div className="text-center text-gray-500 py-12">
                            <span className="text-lg">No jobs found matching your criteria.</span>
                            {(jobSearchTerm || (selectedGroupFilter && selectedGroupFilter !== 'all')) && (
                                <div className="mt-4 flex justify-center gap-3">
                                    {jobSearchTerm && (
                                        <button
                                            onClick={() => setJobSearchTerm('')}
                                            className="text-blue-600 hover:text-blue-800 font-semibold"
                                        >
                                            Clear search filter
                                        </button>
                                    )}
                                    {selectedGroupFilter && selectedGroupFilter !== 'all' && (
                                        <button
                                            onClick={() => setSelectedGroupFilter('all')}
                                            className="text-brand-600 hover:text-brand-800 font-semibold"
                                        >
                                            Clear group filter
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {
                (bulkResults || (previewItems.length > 0 && previewJobId)) && (
                    <div className="card mt-6">
                        <div className="card-header flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">
                                    {bulkResults ? 'Verification Results' : 'Live Results Preview'}
                                </h3>
                                {!bulkResults && <p className="text-xs text-gray-500">Showing last 10 verified emails</p>}
                            </div>
                            {previewJobId && !bulkResults && (
                                <button
                                    onClick={() => navigate('/results', { state: { jobId: previewJobId } })}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    View All Results
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {(bulkResults?.results?.length > 0 || previewItems.length > 0) ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead>
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">email</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">status</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">type</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">node</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(bulkResults?.results || previewItems).map((row, idx) => {
                                                return (
                                                    <tr key={idx} className="hover:bg-gray-50 border-t border-gray-100">
                                                        <td className="px-4 py-2 whitespace-nowrap text-sm">{row.email}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap">
                                                            <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${getStatusColor(row.status)}`}>
                                                                {getStatusIcon(row.status)}
                                                                {row.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{row.type || 'Standard'}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{row.satellite_node_name || (row.is_satellite ? 'Satellite' : 'Master')}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-gray-500 py-10">No results to display yet.</div>
                            )}
                        </div>
                    </div>
                )
            }
            {
                bulkResults && (
                    <div className="card">
                        <div className="card-header">
                            <h3 className="text-lg font-medium text-gray-900">Export Results</h3>
                        </div>
                        <div className="card-body">
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => exportResults('csv')}
                                    className="btn btn-secondary"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export as CSV
                                </button>
                                <button
                                    onClick={() => exportResults('json')}
                                    className="btn btn-secondary"
                                >
                                    <FileText className="h-4 w-4 mr-2" />
                                    Export as JSON
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Delete Job Confirmation Modal */}
            {deleteModalOpen && (
                <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-lg p-6 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center mb-4">
                            <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                            <h3 className="text-lg font-medium text-gray-900">Delete Job</h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this verification job? This action cannot be undone and will permanently remove all associated data.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setDeleteModalOpen(false);
                                    setJobToDelete(null);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                No, Keep Job
                            </button>
                            <button
                                onClick={confirmDeleteJob}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Yes, Delete Job
                            </button>
                        </div>
                    </div>
                </div></Portal>
            )}

            {/* Force Complete Modal */}
            {forceCompleteModalOpen && (
                <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-lg p-6 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center mb-4">
                            <AlertTriangle className="h-6 w-6 text-amber-500 mr-3" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Force Complete Job</h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                            {(pendingForceCompleteRef.current?.stuckRemaining || 0).toLocaleString()} email(s) are still pending/queued and may never finish.
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Force-complete will mark them as <strong>Unknown</strong> and finish this job.
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                            You can re-verify Unknown emails later from the Results page using "Retry Unknowns".
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => { setForceCompleteModalOpen(false); pendingForceCompleteRef.current = null; }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmForceComplete}
                                className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-md hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400"
                            >
                                Yes, Force Complete
                            </button>
                        </div>
                    </div>
                </div></Portal>
            )}

            {/* Cancel Job Confirmation Modal */}
            {cancelModalOpen && (
                <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-lg p-6 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center mb-4">
                            <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                            <h3 className="text-lg font-medium text-gray-900">Cancel Job</h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to cancel this verification job? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    if (isCancellingJob) return;
                                    setCancelModalOpen(false);
                                    setJobToCancel(null);
                                }}
                                className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${isCancellingJob ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                disabled={isCancellingJob}
                            >
                                No, Keep Running
                            </button>
                            <button
                                onClick={confirmCancelJob}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${isCancellingJob ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                                disabled={isCancellingJob}
                            >
                                {isCancellingJob ? (<span className="inline-flex items-center"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cancelling...</span>) : 'Yes, Cancel Job'}
                            </button>
                        </div>
                    </div>
                </div></Portal>
            )}

            {/* Edit Group Modal */}
            {editGroupModalOpen && (
                <Portal><div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => {
                        setEditGroupModalOpen(false);
                        setEditingJob(null);
                    }}></div>
                    <div className="bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-2xl p-6 max-w-md mx-4 relative z-10 w-full shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                    <Tag className="h-5 w-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Assign Category / Group</h3>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                Organize "<span className="font-bold text-gray-700 dark:text-gray-300">{editingJob?.job_name}</span>" by assigning a category.
                            </p>
                            
                            <div className="mb-6">
                                <CreatableSelect
                                    isClearable
                                    placeholder="Select an existing group or type a new one..."
                                    options={groups.map(g => ({ value: g.name || g, label: `${g.name || g} (${g.count || '?'} lists)` }))}
                                    onChange={(newValue) => setEditGroupName(newValue ? newValue.value : '')}
                                    value={editGroupName ? { value: editGroupName, label: editGroupName } : null}
                                    styles={customSelectStyles}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setEditGroupModalOpen(false);
                                        setEditingJob(null);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 dark:bg-gray-850 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors border border-transparent dark:border-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmSaveGroup}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </Portal>
            )}

            {/* How It Works Section */}
            <div className="card mt-8">
                <div className="card-header">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <InfoIcon className="h-6 w-6 text-brand-500" />
                        How It Works
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Our multi-layer verification process ensures maximum accuracy
                    </p>
                </div>
                <div className="card-body">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-brand-100 dark:bg-brand-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Syntax Validation</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Checks for valid email format and structure</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Settings className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">DNS MX Record Check</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Ensures the domain can receive mail</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-warning-100 dark:bg-warning-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Disposable Email Detection</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Flags temporary/throwaway addresses</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Mail className="h-5 w-5 text-success-600 dark:text-success-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">SMTP Handshake Verification</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Attempts to connect to the mail server</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                                <InfoIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Catch-All Domain Detection</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Identifies domains that accept all mail</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-brand-50 to-primary-50 dark:from-brand-900/20 dark:to-primary-900/20 rounded-xl border-2 border-brand-200 dark:border-brand-700 hover:border-brand-400 dark:hover:border-brand-500 transition-colors">
                            <div className="h-10 w-10 bg-gradient-to-br from-brand-500 to-primary-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                                <span className="text-white text-xl">✨</span>
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Advanced Magic Logic</h4>
                                <p className="text-xs text-slate-600 dark:text-slate-400">Proprietary algorithms for even smarter verification 😃</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}